/**
 * FirmaStandOverviewPage.jsx — Firma Tarafı "Standlarım" Detayı
 * Route: /Firma/Standlar/:standId
 *
 * FirmaStandListPage.jsx'in ("Standlarım") TEK drill-down hedefi — bu sayfa
 * FirmaStandDetailPage.jsx'ten (/Firma/Anasayfa/:standId) kasıtlı olarak ayrı
 * bir dosya/route. FirmaStandDetailPage.jsx hâlâ "Projeler Listesi"
 * (FirmaProjelerListPage.jsx) ve Ana Sayfa dashboard'ının proje bilgisi/belge
 * yükleme/taahhütname akışına hizmet ediyor (Proje Bilgiler/Proje Dosyaları/
 * İnşa İzni ve Taahhütnameler sekmeleri, dosya yükleme dahil) — bu sayfa ise
 * Standlarım'ın genel stand özeti amacına hizmet ediyor ve dosya yükleme
 * içermiyor. İkisi de aynı standOverviewService kaydını okur ama farklı
 * amaçlara hizmet ettiği için içerikleri kasıtlı olarak örtüşmüyor.
 *
 * (2026-07-24, `/fromImageToLayout` referans görsel — admin'in StandDetailPage.jsx
 * "Stant Firmalari" tablosu): 3 sekme —
 *  - "Stant Bilgileri": Stand Bilgileri, Stand Tabela Adı (2026-07-31 revizyonuyla
 *    salt-okunur oldu — renkli rozetin yanına taşındı, düzenleme kaldırıldı, bkz.
 *    dosya sonu), Talepler (Teknik Talepler ikon özeti + Talepler
 *    sayfasına link, asıl yönetim FirmaTalepStandDetailPage.jsx'te),
 *    Denetimler (standOverviewService'teki mock `denetimler` — admin'in
 *    StandDetailPage.jsx'teki Saha Modülü linklerinin Firma rolüne kapalı
 *    olduğu için salt-okunur bir geçmiş listesi kondu), Stand Durumu
 *    (admin'in StandDetailPage.jsx'teki aynı 8 alanının — Problemli Stand/İç
 *    Tesisat Onayı/Yasaklı Stand/Islak İmza/Alan Borcu/Proje Borcu/İnternet
 *    Talebi/Cam Kullanımı — salt-okunur rozet kopyası, karar admin tarafına ait).
 *  - "Konum" (2026-07-31 revizyonuna kadar "Kroki" idi, bkz. KonumTab): "Standı
 *    Gör" / "Alanı Gör" anahtarlı. Standı Gör — Teknik Talepler'in (Elektrik/Su/
 *    Hava/İnternet) çıkış noktalarını kategori renginde gösterir, sadece Elektrik
 *    burada tıklanarak işaretlenebilir (aynı veri FirmaTalepStandDetailPage.jsx'teki
 *    Teknik Talepler sekmesiyle paylaşılır, orada dördü de editable). Belge
 *    (standFileId 3) yoksa Talepler sayfasına yönlendirir. Alanı Gör — gerçek bir
 *    salon kroki/konum sistemi henüz yok, bu yüzden şimdilik sadece önizleme/
 *    placeholder (bkz. AlanKrokiPlaceholder).
 *  - "Stand Firmaları ve Taahütnameler": admin'in StandDetailPage.jsx'teki
 *    "Stant Firmalari" tablosunun (bkz. FirmaEsleModal.jsx) salt-okunur firma
 *    karşılığı (Firma/Firma Türü/Eşleme Seviyesi, ekleme/kaldırma admin
 *    tarafına ait) + İnşa İzni ve Taahhütnameler durumu.
 *
 * Açık Revizyonlar kartı standId köprüsüyle (getProjectByStandId) salt-okunur.
 */
import React, { useContext, useEffect, useId, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Badge, Nav, NavItem, NavLink, Spinner, Alert, Button, ButtonGroup,
  Modal, ModalHeader, ModalBody, Input, Table,
  AccordionItem, AccordionHeader, AccordionBody, UncontrolledAccordion,
} from "reactstrap";
import {
  ArrowLeft, Ruler, Maximize2, Layers3, Building2,
  Check, CheckCheck, X, Clock, Image as ImageIcon, Coins, AlertTriangle, Info,
  Zap, Droplet, Wind, Wifi, FileText, ShieldCheck,
  Sparkles, Users, Shield, Cog, PartyPopper, CookingPot, Waves, Flame, PawPrint, Scissors, Crosshair, Armchair,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import MainContext from "../../Components/Contexts/MainContext";
import { getStandOverviewDetail } from "../../services/standOverviewService";
// #439 (2026-10-16): the real plan (fdsVector embed, view-only), the same component as the staff Stand Detay › Konum.
import StandKonumKarti from "../../Components/Kroki/StandKonumKarti";
import {
  getStandTypePath, FILE_APPROVAL_STATUS, getProjectByStandId,
  REVISION_STATUS, REVISION_TARGET_KIND, isRevisionUnfinished,
} from "../../services/projectService";
import {
  getTeknikTalepler, TEKNIK_TALEP_KATEGORILERI,
  getHizmetTalepleri, HIZMET_TALEP_KATEGORILERI,
  getIsIzinleri, IS_IZNI_KATEGORILERI, getEkipmanTalebi, TALEP_STATUS,
  BORC_DURUMU_KATEGORILERI, BORC_DURUMU_STATUS, HIZMET_BORC_META,
} from "../../services/standTalepService";
// WAS (until #388): import { getFirmaProfile, getFirmaDocuments, getFirmaBelgeleri } from "../../services/firmaProfileService";
import { getFirmaEvrakOzeti } from "../../services/firmaProfileService";
import { getStandDurumuOzet, DENETIM_KATEGORILERI, SEVIYE_RENK } from "../../services/standDurumuService";
import RevisionThread from "../../Components/Projects/RevisionThread";
// #506: the Elektrik tile also says who signed (decorator / decorator + electrician); the level comes from the stand list's commitments[].
import ImzaSeviyesiRozeti from "../../Components/Projects/ImzaSeviyesiRozeti";
import { seviyeyiTasi } from "../../Components/Projects/taahhutnameYardimci";
import FirmaEklemeTalebiCard from "../../Components/Firma/FirmaEklemeTalebiCard";
// #489: the four Stand Durumu statuses as compact tiles with one shared "Detaylar" panel.
import StandDurumuKutulari from "../../Components/Firma/StandDurumuKutulari";
// WAS (until #403): import { MOCK_DECORATOR_FIRMA_ID, TEST_DECORATOR_FIRMA_ID } from "../../services/firmaProfileService";
import { MOCK_DECORATOR_FIRMA_ID } from "../../services/firmaProfileService";
// #403: the live branch takes the logged-in company's id from the portal context (GET /portal/context).
import { usePortal } from "../../Components/Contexts/PortalContext";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

// Oturumdaki kullanıcının firması — FirmaAnasayfaListPage.jsx/FirmaKartPage.jsx'teki
// aynı desen (dosya-yerel kopya). Canlı standlarda test dekoratörüne sabitlenir,
// çünkü yerel login mock user nesnesini firmaId ile doldurmuyor.
function getCurrentFirmaId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.firmaId ?? MOCK_DECORATOR_FIRMA_ID;
  } catch {
    return MOCK_DECORATOR_FIRMA_ID;
  }
}

// Canlı standId'ler (StandOverviewDetailPage.jsx/FirmaTalepStandDetailPage.jsx'teki
// aynı desen — mock standId'ler Mongo-tarzı hex string, gerçek olanlar düz sayı,
// bkz. util/knowledgebase/local-backend-testing.md "forceLive pattern").
function isNumericStandId(id) {
  return /^\d+$/.test(String(id ?? ""));
}

// stand_file 5 "Taahütnameler" alt-id 1=Statik/2=İSG/3=Elektrik — dosya-yerel kopya,
// aynısı FirmaStandListPage.jsx/FirmaProjelerListPage.jsx'te de var (bkz. o
// dosyalardaki aynı isimli yorum, bu kod tabanı ortak util yerine küçük kopyaları
// tercih ediyor).
const COMMITMENT_SUBFILE_META = {
  1: { key: "statik", get label() { return i18n.t("portal:standGenel.statikTaahutname"); } },
  2: { key: "isg", get label() { return i18n.t("portal:standGenel.isgTaahutname"); } },
  3: { key: "elektrik", get label() { return i18n.t("portal:standGenel.elektrikTaahutname"); } },
};
function deriveCommitmentsFromProject(bridged) {
  if (!bridged) return [];
  const files = (bridged.uploadedFiles || []).filter((f) => f.standFileId === 5 && COMMITMENT_SUBFILE_META[f.subStandFileId]);
  return Object.entries(COMMITMENT_SUBFILE_META).map(([subId, meta]) => {
    const matches = files.filter((f) => f.subStandFileId === Number(subId));
    const latest = [...matches].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))[0];
    return { key: meta.key, label: meta.label, status: latest?.approvalStatus ?? 0, fileId: latest?.id ?? null };
  });
}

// Canlı standId köprüsü — getStandOverviewDetail'in canlı dalı proje/belge alanlarını
// bilerek dışarıda bırakıyor (bkz. o fonksiyonun dosya başı yorumu), bu sayfa
// seviyesinde projectService.getProjectByStandId sonucuyla birleştiriliyor. Merge
// sonrası `stand.requiredCount`/`approvedCount`/`commitments`/`uploadedFiles`/
// `standTypeId`/`projectHeightNumber` sayfanın geri kalanında hiç değişmeden
// (mock'takiyle aynı alan adlarıyla) okunabiliyor.
function mergeLiveProjectIntoStand(stand, bridged) {
  const requiredCount = bridged?.requiredCount ?? 0;
  const approvedCount = bridged?.approvedCount ?? 0;
  return {
    ...stand,
    requiredCount,
    approvedCount,
    commitments: seviyeyiTasi(deriveCommitmentsFromProject(bridged), stand.commitments),
    uploadedFiles: bridged?.uploadedFiles ?? [],
    standTypeId: bridged?.standTypeId ?? null,
    // projectHeightNumber — bridged projedeki SÖZ VERİLEN (promised) yükseklik;
    // gerçek backend'de m² alanı yok (bkz. standOverviewService.js standArea notu),
    // stand.standArea zaten getStandOverviewDetail'in canlı dalında stand_kroki'den
    // geliyor, burada ezilmiyor.
    projectHeightNumber: bridged?.projectHeightNumber ?? null,
    projectDeclared: bridged?.projeBeyani?.declared === true,
  };
}

const TABS = [
  { key: "info", get label() { return i18n.t("portal:standGenel.stantBilgileri"); } },
  { key: "konum", get label() { return i18n.t("portal:standGenel.konum"); } },
  { key: "firmalar", get label() { return i18n.t("portal:standGenel.standFirmalariVeTaahutnameler"); } },
  { key: "durumu", get label() { return i18n.t("portal:standGenel.standDurumu"); } },
];

// Evraklar/Firma Durumu bölümünde gösterilen firma-çapında belgelerin YEDEK etiketleri (mock) —
// canlıda ad, Firma İSG Belgeleri grubunun kendi satırından gelir (`val.ad`, #258).
const FIRMA_BELGE_LABELS = {
  get firmaIsgTaahhutnamesi() { return i18n.t("portal:standGenel.firmaIsgTaahhutnamesi"); },
  get isYeriHekimiAtamasi() { return i18n.t("portal:standGenel.isYeriHekimiAtamasi"); },
  get isGuvenligiUzmaniAtamasi() { return i18n.t("portal:standGenel.isGuvenligiUzmaniAtamasi"); },
};

// Stand Durumu sekmesinin Borç Durumu satırında gösterilen kategori listesi —
// Talepler Borç Durumu sekmesindeki 3 kategori + Hizmet Borcu (bkz.
// standTalepService.js'teki HIZMET_BORC_META yorumu, o sekme kasıtlı olarak
// değiştirilmedi).
const STAND_DURUMU_BORC_META = [...BORC_DURUMU_KATEGORILERI, HIZMET_BORC_META];

const GRAND_DURUMU_META = {
  projeYok: { get label() { return i18n.t("portal:standGenel.projeHenuzYuklenmedi"); }, get desc() { return i18n.t("portal:standGenel.buStandIcinProjeBilgileri"); }, color: SEVIYE_RENK.projeYok, Icon: Info },
  sorunlu: { get label() { return i18n.t("portal:standGenel.sorunluStand"); }, get desc() { return i18n.t("portal:standGenel.asagidakiAlanlardanEnAzBiri"); }, color: SEVIYE_RENK.sorunlu, Icon: AlertTriangle },
  tamamlandi: { get label() { return i18n.t("portal:standGenel.durumuUygun"); }, get desc() { return i18n.t("portal:standGenel.projeBorcVeTalepDurumu"); }, color: SEVIYE_RENK.tamamlandi, Icon: Check },
  tam: { get label() { return i18n.t("portal:standGenel.tumKontrollerTamam"); }, get desc() { return i18n.t("portal:standGenel.projeBorcEvrakTalepVe"); }, color: SEVIYE_RENK.tam, Icon: CheckCheck },
};

// Revizyon konuşmalarındaki standFileId'yi okunabilir bir etikete çevirir —
// bu sayfada bu belge tiplerine ait bir yükleme bölümü yok, sadece "Açık
// Revizyonlar" kartında/modal başlığında bilgi amaçlı kullanılıyor.
const STAND_FILE_LABELS = { get 2() { return i18n.t("portal:standGenel.statikHesap"); }, get 3() { return i18n.t("portal:standGenel.teknikTalepPlani"); }, get 5() { return i18n.t("portal:standGenel.projeGorselleri"); } };
function revisionFileLabel(standFileId) {
  return STAND_FILE_LABELS[standFileId] ?? `Belge Tipi #${standFileId}`;
}

// Renkler sabit hex — bu temanın çalışma zamanı --bs-*/--vz-* CSS değişkenleri
// yok (bkz. aynı çözüm FirmaProjelerListPage.jsx'teki STATUS_HEX/NEUTRAL_GRAY'de).
const NEUTRAL_GRAY = "#878a99";
const STATUS_HEX = { warning: "#f7b84b", success: "#0ab39c", danger: "#f06548" };

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), { day: "2-digit", month: "short", year: "numeric" });
}

// Sekme başlığı olmayan, art arda dizilen bölümler için hafif ayraç başlığı.
function SectionHeader({ children }) {
  return (
    <h6
      className="text-muted text-uppercase mb-2 mt-4"
      style={{ fontSize: "0.72rem", letterSpacing: "0.06em" }}
    >
      {children}
    </h6>
  );
}

function RequiredFileRibbon({ id, label, children }) {
  return (
    <Card id={id} className="position-relative overflow-hidden h-100">
      <span
        className="badge bg-danger position-absolute text-white"
        style={{
          top: 12, left: -6, padding: "4px 16px 4px 10px",
          clipPath: "polygon(0 0, 100% 0, 88% 50%, 100% 100%, 0 100%)",
          fontSize: "0.7rem", zIndex: 2,
        }}
      >
        {label}
      </span>
      <CardBody className="pt-4">{children}</CardBody>
    </Card>
  );
}

/* WAS (until #439): the mock Konum tab (KrokiShape, StandKrokiView, AlanKrokiPlaceholder, KonumTab); the tab now shows the fdsVector
   plan through Components/Kroki/StandKonumKarti.jsx. Kept as a comment, nothing was deleted.
// Kroki gösterimi — grid arka plan + L şeklinde stand poligonu.
function KrokiShape({ size = 160 }) {
  const gridId = useId();
  return (
    <svg viewBox="0 0 120 120" width={size} height={size}>
      <defs>
        <pattern id={gridId} width="15" height="15" patternUnits="userSpaceOnUse">
          <path d="M 15 0 L 0 0 0 15" fill="none" stroke="#d7dfe3" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="120" height="120" fill={`url(#${gridId})`} />
      <polygon
        points="10,10 65,10 65,65 110,65 110,110 10,110"
        fill="#178f8f" fillOpacity="0.18" stroke="#178f8f" strokeWidth="1.5"
      />
    </svg>
  );
}


// "Konum" sekmesinin "Standı Gör" görünümü (eskiden ProjectKrokiPreview / tek başına
// "Kroki" sekmesiydi, bkz. dosya sonundaki KonumTab'ın 2026-07-31 revizyon notu).
//
// 2026-08-13 revizyonu: bu sayfa (Standlarım detayı) salt-okunur bir genel bakış —
// Teknik Talep çıkış noktalarının GİRİLMESİ/DÜZENLENMESİ FirmaTalepStandDetailPage.jsx'teki
// Teknik Talepler sekmesinin işi (TeknikTalepModal, dört kategori de orada editable).
// Önceden Elektrik burada da (InteractiveKrokiPin ile) tıklanarak işaretlenebiliyordu —
// aynı çıkış noktasının iki farklı sayfadan düzenlenebilmesi kafa karıştırıcıydı ve bu
// sayfanın "salt-okunur özet" amacıyla çelişiyordu, bu yüzden kaldırıldı. Artık sadece
// boş kroki (KrokiShape) gösteriliyor, hiçbir pin çizilmiyor/tıklanamıyor — çıkış
// noktalarını görmek veya değiştirmek isteyen kullanıcı Talepler sayfasına yönlendirilir.
function StandKrokiView({ docExists, onGoToTalepler }) {
  const { t } = useTranslation("portal");
  return (
    <div className="d-flex flex-column align-items-center py-2">
      <KrokiShape size={180} />
      <div className="text-muted small mt-2 text-center">
        {docExists
          ? t("standGenel.talepCikisNoktalariBuradaDuzenlenemez")
          : t("standGenel.talepCikisNoktalariniGormekIsaretlemek")}
      </div>
      {onGoToTalepler && (
        <Button color="link" size="sm" className="mt-1" onClick={onGoToTalepler}>
          {t("standGenel.taleplerSayfasinaGit")}
        </Button>
      )}
    </div>
  );
}

// "Alanı Gör" — henüz gerçek bir salon kroki/konum sistemi yok (backend'de standların
// salon içi x/y koordinatları veya salon taban planı verisi bulunmuyor, bkz.
// KonumTab'ın dosya başı yorumu). Bu yüzden bu görünüm işlevsel değil — sadece o
// sistem tamamlandığında neyin görüneceğinin önizlemesi: standın salondaki YERİ
// (diğer standların adı gösterilmeden, sadece konumları). Konumlar illüstratif/sabit.
function AlanKrokiPlaceholder({ salonName, standCode }) {
  const { t } = useTranslation("portal");
  const otherSpots = [
    { x: 15, y: 20 }, { x: 45, y: 15 }, { x: 78, y: 22 }, { x: 20, y: 55 },
    { x: 62, y: 62 }, { x: 85, y: 75 }, { x: 32, y: 82 }, { x: 70, y: 40 },
  ];
  return (
    <div className="d-flex flex-column align-items-center py-3">
      <Badge color="warning" className="mb-3">{t("standGenel.onizlemeYakinda")}</Badge>
      <svg viewBox="0 0 100 100" width={220} height={220} className="border rounded-3 bg-light">
        {otherSpots.map((p, i) => (
          <rect key={i} x={p.x - 4} y={p.y - 4} width={8} height={8} rx={1.5} fill="#adb5bd" opacity={0.6} />
        ))}
        <rect x={46} y={46} width={10} height={10} rx={2} fill="#178f8f" stroke="#fff" strokeWidth="1" />
      </svg>
      <div className="text-muted small text-center mt-3" style={{ maxWidth: 340 }}>
        {t("standGenel.krokiKonumSistemiTamamlandigindaBurada")}{" "}<strong>{salonName}</strong>{" "}{t("standGenel.salonununTabanPlaniStandinizin")}<strong>{standCode}</strong>{t("standGenel.yeriIsaretlenmisHaldeGorunecekDiger")}
      </div>
    </div>
  );
}

// "Konum" sekmesi (eskiden "Kroki") — 2026-07-31 revizyonu (`/revise-page`):
// "Standı Gör" (bu standın kendi krokisi + Talep çıkış noktaları, StandKrokiView) /
// "Alanı Gör" (salondaki yeri, AlanKrokiPlaceholder — henüz önizleme) arasında
// geçiş anahtarı.
function KonumTab({ standId, stand, navigate }) {
  const { t } = useTranslation("portal");
  const [view, setView] = useState("stand");
  const docExists = (stand.uploadedFiles || []).some((f) => f.standFileId === 3);

  return (
    <div>
      <div className="d-flex justify-content-center mb-3">
        <ButtonGroup>
          <Button color="info" outline={view !== "stand"} onClick={() => setView("stand")}>
            {t("standGenel.standiGor")}
          </Button>
          <Button color="info" outline={view !== "alan"} onClick={() => setView("alan")}>
            {t("standGenel.alaniGor")}
          </Button>
        </ButtonGroup>
      </div>
      {view === "stand" ? (
        <RequiredFileRibbon label={t("standGenel.standiGor")}>
          <StandKrokiView
            docExists={docExists}
            onGoToTalepler={() => navigate(`/Firma/Talepler/${standId}`)}
          />
        </RequiredFileRibbon>
      ) : (
        <RequiredFileRibbon label={t("standGenel.alaniGor")}>
          <AlanKrokiPlaceholder salonName={stand.salonName} standCode={stand.standCode || stand.standName} />
        </RequiredFileRibbon>
      )}
    </div>
  );
}
*/

// Teknik Talep Planı (standFileId 3) için: dört Teknik Talep kategorisinin
// (Elektrik/Su/Hava/İnternet) hasRequest/miktar durumunu gösteren ikon karesi.
// Not: standTalepService.js'e göre bu dört kalemin onay akışı yok (miktar
// bazlı) — bu yüzden burada "onaylı/reddedildi" değil, talep var/yok + miktar
// gösteriliyor (FirmaTalepStandDetailPage.jsx'teki CategoryToolbar ile aynı
// görsel dil).
const TEKNIK_TALEP_ICONS = { elektrik: Zap, su: Droplet, hava: Wind, internet: Wifi };
const HIZMET_TALEP_ICONS = { "24saatElektrik": Clock, temizlik: Sparkles, hostHostes: Users, guvenlik: Shield };
const IS_IZNI_TALEP_ICONS = {
  makine: Cog, gosteri: PartyPopper, pisirme: CookingPot, sivi: Waves,
  sicak: Flame, hayvan: PawPrint, kesici: Scissors, silah: Crosshair,
};

// Gruplar arası dikey ayraç + ikon-kare tile — FirmaTaleplerListPage.jsx'teki
// "Talepler Listesi" kart satırıyla (StandTalepTiles/VDivider/TalepTile) aynı
// görsel dil, dosya-yerel kopya: o sayfadaki sabit-genişlik rezerve slot/taşma
// mantığı ("+N" rozeti vb.) burada yok — orası çok satırlı bir listeyi hizalı
// tutmak için gerekliydi, burada stand başına tek bir özet bloğu var.
function TalepGrupAyraci() {
  return (
    <div className="d-none d-md-block" style={{ width: 1, alignSelf: "stretch", backgroundColor: "currentColor", opacity: 0.15 }} />
  );
}

function TalepIkonKare({ Icon, color, opacity = 1, label }) {
  return (
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{ width: 36, height: 36, background: color, opacity }}
      title={label}
    >
      <Icon size={16} color="#fff" />
    </div>
  );
}

// "Talepler" bölümü — dört talep kategorisinin (Teknik/Hizmet/İş İzni/Ekipman)
// tümünü özetler (önceki sürüm sadece Teknik Talepler'i gösteriyordu). Tam
// yönetim/oluşturma için hâlâ FirmaTalepStandDetailPage.jsx'e link — o sayfanın
// 5 sekmesi burada tekrarlanmıyor.
function TalepOzetSection({ standId, navigate, isLive }) {
  const { t: tx } = useTranslation("portal");
  const [data, setData] = useState(null);

  useEffect(() => {
    let ignore = false;
    Promise.all([
      getTeknikTalepler(standId, { forceLive: isLive }),
      getHizmetTalepleri(standId, { forceLive: isLive }),
      getIsIzinleri(standId, { forceLive: isLive }),
      getEkipmanTalebi(standId, { forceLive: isLive }),
    ])
      .then(([teknik, hizmet, isIzin, ekipman]) => {
        if (!ignore) setData({ teknik, hizmet, isIzin, ekipman });
      })
      .catch(() => {
        if (!ignore) setData({ teknik: [], hizmet: [], isIzin: [], ekipman: null });
      });
    return () => { ignore = true; };
  }, [standId, isLive]);

  const byTeknik = {};
  (data?.teknik || []).forEach((r) => { byTeknik[r.categoryKey] = r; });

  const teknikTiles = TEKNIK_TALEP_KATEGORILERI.map((cat) => {
    const req = byTeknik[cat.key];
    const hasRequest = !!req?.hasRequest;
    // bkz. TalepQuantityCard'daki (FirmaTalepStandDetailPage.jsx) aynı koruma —
    // Elektrik'te de quantity hiç deklare edilmeden hasRequest true olabilir.
    const quantityLabel = req?.quantity ? `${req.quantity.value} ${req.quantity.unit}` : tx("standGenel.talepEdildi");
    return {
      id: cat.key, Icon: TEKNIK_TALEP_ICONS[cat.key],
      color: hasRequest ? cat.color : NEUTRAL_GRAY, opacity: hasRequest ? 1 : 0.55,
      label: hasRequest ? `${cat.label}: ${quantityLabel}` : tx("standGenel.talepEdilmedi", { label: cat.label }),
    };
  });

  const hizmetTiles = (data?.hizmet || []).filter((r) => r.hasRequest).map((r) => {
    const cat = HIZMET_TALEP_KATEGORILERI.find((c) => c.key === r.categoryKey);
    return { id: r.categoryKey, Icon: HIZMET_TALEP_ICONS[r.categoryKey], color: cat.color, label: cat.label };
  });

  const isIzinTiles = (data?.isIzin || []).filter((r) => r.hasRequest).map((r) => {
    const cat = IS_IZNI_KATEGORILERI.find((c) => c.key === r.categoryKey);
    const statusInfo = TALEP_STATUS[r.status] ?? TALEP_STATUS[0];
    return { id: r.categoryKey, Icon: IS_IZNI_TALEP_ICONS[r.categoryKey], color: statusInfo.hex, label: `${cat.label} — ${statusInfo.label}` };
  });

  const ekipmanTiles = data?.ekipman?.hasRequest ? [(() => {
    const statusInfo = TALEP_STATUS[data.ekipman.status] ?? TALEP_STATUS[0];
    return { id: "ekipman", Icon: Armchair, color: statusInfo.hex, label: tx("standGenel.ekipman", { label: statusInfo.label }) };
  })()] : [];

  const digerGruplar = [hizmetTiles, isIzinTiles, ekipmanTiles];
  const digerVarMi = digerGruplar.some((g) => g.length > 0);

  return (
    <RequiredFileRibbon label={tx("standGenel.talepler")}>
      <div className="w-100">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="text-muted small">{tx("standGenel.tumTalepler")}</div>
          <Button color="info" outline size="sm" onClick={() => navigate(`/Firma/Talepler/${standId}`)}>
            {tx("standGenel.tumTalepleriGoruntule")}
          </Button>
        </div>
        {!data ? (
          <div className="text-center py-2"><Spinner color="primary" size="sm" /></div>
        ) : (
          <div className="d-flex align-items-center flex-wrap gap-2">
            {teknikTiles.map((t) => <TalepIkonKare key={t.id} {...t} />)}
            {digerGruplar.filter((g) => g.length > 0).map((group, i) => (
              <React.Fragment key={i}>
                <TalepGrupAyraci />
                {group.map((t) => <TalepIkonKare key={t.id} {...t} />)}
              </React.Fragment>
            ))}
            {!digerVarMi && <span className="text-muted small ms-1">{tx("standGenel.hizmetIsIzniEkipmanTalebi")}</span>}
          </div>
        )}
      </div>
    </RequiredFileRibbon>
  );
}

function CommitmentTile({ id, label, status, file, level, elektrikciName }) {
  const { t } = useTranslation("portal");
  const info = FILE_APPROVAL_STATUS[status] ?? FILE_APPROVAL_STATUS[0];
  const Icon = status === 1 ? Check : status === 2 ? X : Clock;
  return (
    <Col id={id} md={4} className="text-center mb-3">
      <div
        className="d-inline-flex align-items-center justify-content-center rounded-3 mb-2"
        style={{ width: 50, height: 50, background: STATUS_HEX[info.color] ?? NEUTRAL_GRAY }}
        title={info.label}
      >
        <Icon size={22} color="#fff" />
      </div>
      <div className="small">{label}</div>
      <ImzaSeviyesiRozeti level={level} elektrikciName={elektrikciName} className="mt-1" />
      {file ? (
        <div className="d-flex align-items-center justify-content-center gap-1 mt-1 text-muted small" title={file.originalFileName}>
          <ImageIcon size={12} />
          <span className="text-truncate" style={{ maxWidth: 140 }}>{file.originalFileName}</span>
        </div>
      ) : (
        <div className="text-muted small mt-1 fst-italic">{t("standGenel.belgeSecilmedi")}</div>
      )}
    </Col>
  );
}

// "Denetimler" bölümü — standOverviewService.js'teki `denetimler` mock alanı
// (kategori başına `kayitlar` geçmişi, bkz. DENETIM_KATEGORILERI), salt-okunur
// düz kronolojik liste. Admin'in StandDetailPage.jsx'teki "Saha Modülü"
// linklerinin (ISG/Elektrik/Acil Durum/... admin denetim modüllerine) firma
// tarafında karşılığı yok (bu modüller Firma rolüne kapalı), bu yüzden burada
// gerçek bir denetim geçmişi listesi gösteriliyor. Stand Durumu sekmesindeki
// kategorize/accordion görünümden (DenetimDurumuAccordion) kasıtlı olarak ayrı —
// burası hızlı bir kronolojik özet, orası kategori bazlı durum + geçmiş detayı.
const DENETIM_SONUC_META = {
  uygun: { get label() { return i18n.t("portal:standGenel.uygun"); }, color: "success" },
  uygunsuzluk: { get label() { return i18n.t("portal:standGenel.uygunsuzluk"); }, color: "danger" },
};

// `stand.denetimler`'in kategorize şeklini (kategori + kayitlar dizisi) bu
// bölümün beklediği düz satır listesine çevirir.
function flattenDenetimler(denetimler) {
  const labelByKey = Object.fromEntries(DENETIM_KATEGORILERI.map((c) => [c.key, c.label]));
  return (denetimler || []).flatMap((kat) => (
    (kat.kayitlar || []).map((k) => ({ ...k, kategori: labelByKey[kat.kategori] ?? kat.kategori }))
  ));
}

function DenetimlerSection({ denetimler }) {
  const { t } = useTranslation("portal");
  return (
    <RequiredFileRibbon label={t("standGenel.denetimler")}>
      <div className="w-100">
        {(denetimler || []).length === 0 ? (
          <div className="text-muted small">{t("standGenel.buStandIcinHenuzDenetim")}</div>
        ) : (
          <Table borderless size="sm" className="mb-0 align-middle" responsive>
            <thead>
              <tr className="text-muted small">
                <th>{t("standGenel.kategori")}</th>
                <th>{t("standGenel.sonuc")}</th>
                <th>{t("standGenel.tarih")}</th>
                <th>{t("standGenel.denetci")}</th>
              </tr>
            </thead>
            <tbody>
              {denetimler.map((d) => {
                const meta = DENETIM_SONUC_META[d.sonuc] ?? DENETIM_SONUC_META.uygun;
                return (
                  <tr key={d.id}>
                    <td>{d.kategori}</td>
                    <td><Badge color={meta.color}>{meta.label}</Badge></td>
                    <td className="text-muted small">{fmtDate(d.tarih)}</td>
                    <td className="text-muted small">{d.denetci}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </RequiredFileRibbon>
  );
}

// "Fotoğraflar" bölümü — standOverviewService.js'teki `photos` mock alanı, "Stant
// Bilgileri" sekmesinin en altında eskiden duran admin'in 8 alanlık "Stand Durumu"
// rozet kopyasının (StandDurumuSection, artık kaldırıldı) yerini alıyor — o bilgi
// artık kendi sekmesinde (bkz. StandDurumuTab), burada tekrarlanmıyor. Bu sayfada
// bir yükleme akışı yok (dosya-yerel not, bkz. dosya başı yorumu) — salt-okunur
// galeri; gerçek görsel dosyası/URL'i olmadığından ImageIcon + başlık/tarih kartı
// gösteriliyor.
function FotograflarSection({ photos }) {
  const { t } = useTranslation("portal");
  return (
    <RequiredFileRibbon label={t("standGenel.fotograflar")}>
      <div className="w-100">
        {(photos || []).length === 0 ? (
          <div className="text-muted small">{t("standGenel.buStandIcinHenuzFotograf")}</div>
        ) : (
          <Row className="g-3">
            {photos.map((p) => (
              <Col xs={6} md={3} key={p.id}>
                <div className="border rounded-3 d-flex flex-column align-items-center justify-content-center py-4 bg-light">
                  <ImageIcon size={28} className="text-muted mb-2" />
                  <div className="small text-center px-1">{p.caption}</div>
                  <div className="text-muted small">{fmtDate(p.uploadedAt)}</div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </RequiredFileRibbon>
  );
}

// ─── "Stand Durumu" sekmesi (aggregate) — proje/borç/evrak/talep/denetim
// özetini standDurumuService.js'ten okuyup gösterir. Yukarıdaki StandDurumuSection'dan
// (admin'in 8 alanının salt-okunur kopyası) kasıtlı olarak ayrı: o zaten var olan
// basit bir alan aynası, bu ise beş farklı kaynaktan türetilen hesaplanmış bir özet.

// #489: DurumBadge and DurumBox (the 2x2 grid of always-open boxes) moved to
// Components/Firma/StandDurumuKutulari.jsx: four compact tiles in a row, details behind "Detaylar".

function GrandIndicatorCard({ seviye }) {
  const meta = GRAND_DURUMU_META[seviye] ?? GRAND_DURUMU_META.sorunlu;
  const { Icon } = meta;
  return (
    <Card className="mb-3 border-0 text-white" style={{ background: meta.color }}>
      <CardBody className="d-flex align-items-center gap-3">
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{ width: 56, height: 56, background: "rgba(255,255,255,0.25)" }}
        >
          <Icon size={28} />
        </div>
        <div>
          <div className="fs-5 fw-bold">{meta.label}</div>
          <div className="small">{meta.desc}</div>
        </div>
      </CardBody>
    </Card>
  );
}

function ProjeDurumuDetay({ stand, izinVar }) {
  const { t } = useTranslation("portal");
  const commitments = stand.commitments || [];
  const approved = commitments.filter((c) => c.status === 1).length;
  return (
    <div className="text-muted small mt-2">
      {t("standGenel.insaatIzni")}
      <span className={izinVar ? "text-success fw-semibold" : "text-danger fw-semibold"}>
        {izinVar ? t("standGenel.alindi") : t("standGenel.alinmadi")}
      </span>
      {t("standGenel.taahhutnamelerOnayli", { approved, length: commitments.length })}
    </div>
  );
}

function BorcMiniList({ borcItems }) {
  const byKey = {};
  (borcItems || []).forEach((i) => { byKey[i.categoryKey] = i; });
  return (
    <div className="d-flex flex-wrap gap-2 mt-2">
      {STAND_DURUMU_BORC_META.map((cat) => {
        const item = byKey[cat.key];
        const status = item?.status ?? "yok";
        const info = BORC_DURUMU_STATUS[status] ?? BORC_DURUMU_STATUS.yok;
        return (
          <Badge key={cat.key} style={{ background: info.hex }} className="fw-normal">
            {cat.label}: {status === "bekliyor" ? `${item.amount} ₺` : info.label}
          </Badge>
        );
      })}
    </div>
  );
}

function EvrakDurumuDetay({ profile, firmaBelgeleri }) {
  const { t } = useTranslation("portal");
  if (!profile) {
    return <div className="text-muted small mt-2">{t("standGenel.buDekoratorIcinFirmaEvrak")}</div>;
  }
  const items = [
    { label: t("standGenel.imzaSirkusu"), ok: profile.imzaSirkusuStatus === "tamamlandi" },
    ...Object.entries(firmaBelgeleri || {}).map(([key, val]) => (
      { label: val.ad ?? FIRMA_BELGE_LABELS[key] ?? key, ok: val.ok ?? val.status === 1 }
    )),
  ];
  return (
    <div className="row row-cols-2 g-2 mt-2">
      {items.map((it) => (
        <div className="col" key={it.label}>
          <Badge color={it.ok ? "success" : "danger"} className="fw-normal d-block w-100 py-1">{it.label}</Badge>
        </div>
      ))}
    </div>
  );
}

// "Kontrol Formu" — kategorilerden bağımsız, stand başına tek bir bayrak
// (standOverviewService.js'teki `stand.kontrolFormu`). Denetim Durumu'nun genel
// geçme şartlarından biri (bkz. standDurumuService.js'teki denetimDurumuOk) ama
// kendi kategorisi/geçmişi yok, bu yüzden accordion'un dışında tek satır olarak
// gösteriliyor.
function KontrolFormuStatus({ kontrolFormu }) {
  const { t } = useTranslation("portal");
  if (!kontrolFormu) return <div className="text-muted small mt-2">{t("standGenel.kontrolFormuHenuzUygulanmadi")}</div>;
  const passed = kontrolFormu.gecti === true;
  return (
    <div className="d-flex align-items-center gap-2 small mt-2">
      <Badge color={passed ? "success" : "danger"}>{passed ? t("standGenel.kontrolFormuBasarili") : t("standGenel.kontrolFormuBasarisiz")}</Badge>
      <span className="text-muted">{fmtDate(kontrolFormu.tarih)} — {kontrolFormu.denetci}</span>
    </div>
  );
}

function DenetimKayitTablosu({ kayitlar }) {
  const { t } = useTranslation("portal");
  if (!kayitlar || kayitlar.length === 0) {
    return <div className="text-muted small py-2">{t("standGenel.henuzDenetimKaydiYok")}</div>;
  }
  return (
    <Table borderless size="sm" className="mb-0 align-middle" responsive>
      <thead>
        <tr className="text-muted small">
          <th>{t("standGenel.sonuc")}</th>
          <th>{t("standGenel.tarih")}</th>
          <th>{t("standGenel.denetci")}</th>
        </tr>
      </thead>
      <tbody>
        {kayitlar.map((k) => {
          const meta = DENETIM_SONUC_META[k.sonuc] ?? DENETIM_SONUC_META.uygun;
          return (
            <tr key={k.id}>
              <td><Badge color={meta.color}>{meta.label}</Badge></td>
              <td className="text-muted small">{fmtDate(k.tarih)}</td>
              <td className="text-muted small">{k.denetci}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

// Denetim Durumu kutusunun içeriği — üç sabit kategori (standDurumuService.js'teki
// DENETIM_KATEGORILERI: İSG/Proje/BetterStands) Teknik Talepler'deki "her zaman
// 4 kalem" kuralıyla aynı mantıkla her zaman gösterilir, veri yoksa kategori
// "Henüz denetim kaydı yok" ile boş açılır. Her kategori kendi ziyaret
// geçmişini (`kayitlar`) taşır, accordion başlığındaki rozet en son ziyaretin
// sonucu (bkz. denetimDurumuOk'taki aynı "son kayıt geçerli" kuralı) — tek
// seferde sadece bir kategori açık kalır (varsayılan Accordion davranışı).
function DenetimDurumuAccordion({ denetimler }) {
  const { t } = useTranslation("portal");
  const byKey = {};
  (denetimler || []).forEach((k) => { byKey[k.kategori] = k; });

  return (
    <UncontrolledAccordion className="mt-2">
      {DENETIM_KATEGORILERI.map((cat) => {
        const kayitlar = byKey[cat.key]?.kayitlar || [];
        const son = kayitlar[kayitlar.length - 1];
        const sonMeta = son ? (DENETIM_SONUC_META[son.sonuc] ?? DENETIM_SONUC_META.uygun) : null;
        return (
          <AccordionItem key={cat.key}>
            <AccordionHeader targetId={cat.key}>
              <span className="flex-grow-1">{cat.label}</span>
              <Badge color={sonMeta?.color ?? "secondary"} className="me-2">
                {sonMeta?.label ?? t("standGenel.veriYok")}
              </Badge>
            </AccordionHeader>
            <AccordionBody accordionId={cat.key}>
              <DenetimKayitTablosu kayitlar={kayitlar} />
            </AccordionBody>
          </AccordionItem>
        );
      })}
    </UncontrolledAccordion>
  );
}

function StandDurumuTab({
  stand, izinVar, ozet, ozetLoading, ozetError, standId, navigate, setActiveTab,
  firmaProfile, firmaBelgeleri,
}) {
  const { t } = useTranslation("portal");
  if (ozetLoading) {
    return <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>;
  }
  if (ozetError) {
    return <Alert color="danger" className="mb-0">{ozetError}</Alert>;
  }
  if (!ozet) return null;

  return (
    <div>
      <GrandIndicatorCard seviye={ozet.seviye} />

      <StandDurumuKutulari
        kutular={[
          {
            key: "proje", icon: Building2, label: t("standGenel.insaatIzniDurumu"), value: ozet.proje,
            details: <ProjeDurumuDetay stand={stand} izinVar={izinVar} />,
            actionLabel: t("standGenel.detayaGit"), onAction: () => setActiveTab("firmalar"),
          },
          {
            key: "borc", icon: Coins, label: t("standGenel.borcDurumu"), value: ozet.borc,
            details: <BorcMiniList borcItems={ozet.borcItems} />,
            actionLabel: t("standGenel.borcDetayi"), onAction: () => navigate(`/Firma/Talepler/${standId}?tab=talepBorcDurumu`),
          },
          {
            key: "evrak", icon: FileText, label: t("standGenel.firmaIsgEvraklariDurumu"), value: ozet.evrak,
            details: <EvrakDurumuDetay profile={firmaProfile} firmaBelgeleri={firmaBelgeleri} />,
            actionLabel: t("standGenel.evraklaraGit"), onAction: () => navigate("/Firma/FirmaKart?tab=evraklar"),
          },
          {
            key: "denetim", icon: ShieldCheck, label: t("standGenel.denetimDurumu"), value: ozet.denetim,
            details: (
              <>
                <KontrolFormuStatus kontrolFormu={stand.kontrolFormu} />
                <DenetimDurumuAccordion denetimler={stand.denetimler} />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}

// "Stand Firmaları ve Taahütnameler" sekmesinin firma tablosu — admin'in
// StandDetailPage.jsx'teki "Stant Firmalari" tablosunun (bkz. FirmaEsleModal.jsx)
// salt-okunur firma karşılığı. Eşleme (ekle/kaldır) admin tarafına ait bir
// aksiyon olarak kalıyor, bu yüzden burada "Firma Eşle" butonu ve İşlemler
// sütunu yok.
function StantFirmalariCard({ firmalar }) {
  const { t } = useTranslation("portal");
  const [search, setSearch] = useState("");
  const filtered = (firmalar || []).filter(
    (f) => !search || f.firmaName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h6 className="mb-0 fw-semibold">{t("standGenel.stantFirmalari")}</h6>
          <Input
            type="text" bsSize="sm" placeholder={t("standGenel.ara")} value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 220 }}
          />
        </div>
        <Table bordered responsive className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>{t("standGenel.firma")}</th>
              <th>{t("standGenel.firmaTuru")}</th>
              <th>{t("standGenel.eslemeSeviyesi")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-muted py-4">
                  {search ? t("standGenel.sonucBulunamadi") : t("common:kayitYok")}
                </td>
              </tr>
            ) : filtered.map((f) => (
              <tr key={f.firmaId}>
                <td>{f.firmaName}</td>
                <td>{f.firmaTypeName}</td>
                <td>{f.eslesmeSeviyesi}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

export default function FirmaStandOverviewPage() {
  const { t: tx } = useTranslation("portal");
  const { standId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusKategori = searchParams.get("kategori");
  const requestedTab = searchParams.get("tab");
  const { selectedFuarId } = useContext(MainContext);
  const portal = usePortal();
  const isLive = isNumericStandId(standId);

  const [stand, setStand] = useState(null);
  const [standTypeName, setStandTypeName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    if (requestedTab === "firmalar" || requestedTab === "commitments") return "firmalar";
    if (requestedTab === "konum" || requestedTab === "kroki") return "konum";
    if (requestedTab === "durumu") return "durumu";
    return "info";
  });
  // standId köprüsüyle bulunan projectService.js kaydı — çoğu stand için null kalır
  // (bkz. FirmaStandDetailPage.jsx'teki aynı köprü notu). Salt-okunur: buradan
  // hiçbir mutasyon yapılmaz.
  const [bridgedProject, setBridgedProject] = useState(null);
  const [activeRevision, setActiveRevision] = useState(null);
  // "Stand Durumu" sekmesi — dekoratörün kendi firma evrak durumu (stand.dekoratorFirmaId
  // üzerinden, standDurumuService.js'in evrakDurumuOk'una geçilir) + beş alt durumun
  // birleşik özeti. Test Decorator (500) dışındaki dekoratörler için firma profili mock
  // verisinde yoktur — bu durumda evrak "Veri Yok" (null) kalır, sayfa çökmez.
  const [firmaProfile, setFirmaProfile] = useState(null);
  const [firmaBelgeleri, setFirmaBelgeleri] = useState({});
  const [standDurumuOzet, setStandDurumuOzet] = useState(null);
  const [ozetLoading, setOzetLoading] = useState(true);
  const [ozetError, setOzetError] = useState(null);

  useEffect(() => { document.title = i18n.t("portal:standGenel.standDetayiFds"); }, []);

  useEffect(() => {
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const detail = await getStandOverviewDetail(standId, { forceLive: isLive });
        if (ignore) return;

        let bridged = null;
        if (detail.standId) {
          try {
            bridged = await getProjectByStandId(detail.standId, { forceLive: isLive });
          } catch {
            bridged = null;
          }
        }
        if (ignore) return;
        setBridgedProject(bridged);

        // Canlı standlar için proje köprüsü stand üzerine merge edilir (bkz. dosya
        // başı mergeLiveProjectIntoStand yorumu) — mock standlar için stand zaten
        // her şeyi tek objede taşıyor, merge no-op.
        const merged = isLive ? mergeLiveProjectIntoStand(detail, bridged) : detail;
        setStand(merged);

        if (merged.standTypeId) {
          const path = await getStandTypePath(merged.standTypeId, { forceLive: isLive });
          if (!ignore) setStandTypeName(path?.[path.length - 1]?.standTypeName ?? null);
        }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [standId, isLive]);

  useEffect(() => {
    if (!stand) return;
    let ignore = false;

    async function run() {
      setOzetLoading(true);
      setOzetError(null);
      let profile = null;
      let documents = [];
      let belgeler = {};
      if (stand.dekoratorFirmaId) {
        try {
          // #388: tek profile-page okuması (?fuarId= ile) — GET /documents başka firma için 403'tü ve tüm özeti "Veri Yok"
          // yapıyordu. kitle HERKES ise belge durumu saklanmıştır: profile null döner, "Veri Yok" kalır.
          // WAS: [profile, documents, belgeler] = await Promise.all([
          //   getFirmaProfile(stand.dekoratorFirmaId, { forceLive: isLive }),
          //   getFirmaDocuments(stand.dekoratorFirmaId, { forceLive: isLive, fuarId: selectedFuarId }),
          //   getFirmaBelgeleri(stand.dekoratorFirmaId, { forceLive: isLive }),
          // ]);
          ({ profile, documents, firmaBelgeleri: belgeler } = await getFirmaEvrakOzeti(
            stand.dekoratorFirmaId,
            { forceLive: isLive, fuarId: selectedFuarId },
          ));
        } catch {
          // Bu dekoratör için mock firma profili yoksa Evraklar/Firma Durumu "Veri
          // Yok" (null) olarak kalır, bkz. standDurumuService.js'teki evrakDurumuOk.
        }
      }
      if (ignore) return;
      setFirmaProfile(profile);
      setFirmaBelgeleri(belgeler);
      try {
        const result = await getStandDurumuOzet(stand, { profile, documents, firmaBelgeleri: belgeler }, { forceLive: isLive });
        if (!ignore) setStandDurumuOzet(result);
      } catch (e) {
        if (!ignore) setOzetError(e.message);
      } finally {
        if (!ignore) setOzetLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [stand, isLive, selectedFuarId]);

  // ?kategori=<key> ile gelindiyse (FirmaProjelerListPage.jsx'teki taahhütname
  // kutucukları) ilgili taahhütname kartına scroll edip kısa süreliğine
  // kenarlıkla vurgular. ?kategori yoksa ama ?tab=commitments ise
  // (FirmaStandListPage.jsx'teki İnşa İzni/Borç Durumu kareleri) "İnşa İzni ve
  // Taahhütnameler" bloğunun tamamına kaydırır. İkisi de "firmalar" sekmesinde
  // yaşıyor — activeTab zaten yukarıda oraya çözümlendiği için anchor DOM'da
  // mevcut olur.
  useEffect(() => {
    if (loading || !stand || activeTab !== "firmalar") return;
    let el = null;
    if (focusKategori) {
      el = document.getElementById(`proje-anchor-commitments-${focusKategori}`);
    } else if (requestedTab === "commitments") {
      el = document.getElementById("proje-anchor-commitments-section");
    }
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("border-primary");
    el.style.borderWidth = "2px";
    const timer = setTimeout(() => {
      el.classList.remove("border-primary");
      el.style.borderWidth = "";
    }, 2500);
    return () => clearTimeout(timer);
  }, [loading, stand, activeTab, focusKategori, requestedTab]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5"><Spinner color="primary" /></Container>
      </div>
    );
  }

  if (error && !stand) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{error}</Alert>
          <Button color="secondary" outline size="sm" onClick={() => navigate("/Firma/Standlar")}>
            <ArrowLeft size={14} className="me-1" />{" "}{tx("standGenel.listeyeDon")}
          </Button>
        </Container>
      </div>
    );
  }

  if (!stand) return null;

  const salonNumber = stand.salonName?.match(/\d+/)?.[0] ?? stand.salonName;
  const izinVar = stand.requiredCount > 0 && stand.approvedCount === stand.requiredCount;
  // ⚠️ "Açık" = KAPANMAMIŞ (status !== 'resolved'), sadece OPEN değil: yanıtlanmış
  // ama henüz karara bağlanmamış revizyonlar da burada kalmalı, yoksa dekoratörün
  // gönderdiği öneri listeden düşer ve iz bırakmadan kaybolur.
  const openRevisions = (bridgedProject?.revisions || []).filter(isRevisionUnfinished);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("standGenel.stand", { standName: stand.standName })} pageTitle={tx("standGenel.firma")} />

        {error && <Alert color="danger">{error}</Alert>}

        <Button color="secondary" outline size="sm" className="mb-3" onClick={() => navigate("/Firma/Standlar")}>
          <ArrowLeft size={14} className="me-1" />{" "}{tx("standGenel.listeyeDon")}
        </Button>

        <Card className="mb-4 overflow-hidden">
          <div className="bg-light">
            <div className="p-4">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* Arka plan sabit değil — Stand Durumu sekmesindeki (standDurumuService.js
                    `seviye`) aynı rengi taşır, bkz. FirmaStandListPage.jsx'teki StandThumbnail'in
                    aynı kaynağı kullanan kartı. */}
                <div
                  className="rounded-3 text-center text-white px-3 py-2 shadow-sm"
                  style={{ minWidth: 72, background: SEVIYE_RENK[standDurumuOzet?.seviye] ?? SEVIYE_RENK.projeYok }}
                >
                  <div className="fw-bold fs-5 lh-1">{salonNumber}</div>
                  <div className="fw-semibold small lh-1 mt-1">{stand.standName}</div>
                </div>
                <div>
                  <h4 className="text-dark mb-0">{stand.firmaName}</h4>
                  <div className="text-muted small">{stand.dekoratorFirmaName || "—"}</div>
                  {/* Salt-okunur — düzenleme kaldırıldı (bkz. /revise-page kararı),
                      renkli rozetin hemen yanında görünsün diye buraya taşındı.
                      `tabelaAdi` — standCode'dan (rengi rozetin kendisinde) AYRI bir
                      alan, bkz. standOverviewService.js'teki aynı isimli yorum;
                      önceki sürümde bu satır yanlışlıkla standCode gösteriyordu. */}
                  <div className="text-muted small mt-1">
                    {/* WAS (#208 öncesi): Stand Tabela Adı: */}
                    {tx("standGenel.standAdi")}{" "}<span className="fw-semibold text-dark">{stand.tabelaAdi || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
            <Nav pills className="px-4 gap-2 border-0" style={{ position: "relative" }}>
              {TABS.map((t) => (
                <NavItem key={t.key}>
                  <NavLink
                    active={activeTab === t.key}
                    className={activeTab === t.key ? "bg-danger text-white" : "bg-white text-dark"}
                    style={{ cursor: "pointer", borderRadius: "6px 6px 0 0" }}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
        </Card>

        {/* ─── Açık Revizyonlar — sekmeden bağımsız, her zaman görünür. ─── */}
        {openRevisions.length > 0 && (
          <Card className="mb-4 border-danger">
            <CardBody>
              <div className="d-flex align-items-center gap-2 mb-3">
                <AlertTriangle size={18} className="text-danger" />
                <h6 className="mb-0">{tx("standGenel.acikRevizyonlar")}</h6>
              </div>
              <div className="d-flex flex-column gap-2">
                {openRevisions.map((r) => (
                  <div key={r._id} className="border rounded p-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <Badge color={REVISION_TARGET_KIND[r.targetKind]?.color ?? "secondary"}>
                          {REVISION_TARGET_KIND[r.targetKind]?.label ?? r.targetKind}
                        </Badge>
                        <Badge color={REVISION_STATUS[r.status]?.color ?? "secondary"}>
                          {r.status === "answered" ? tx("standGenel.incelemede") : (REVISION_STATUS[r.status]?.label ?? r.status)}
                        </Badge>
                        <span className="small text-muted">
                          {r.standFileId != null ? revisionFileLabel(r.standFileId) : tx("standGenel.projeninGeneli")}
                        </span>
                      </div>
                      <div className="small">{r.messages[0]?.text}</div>
                    </div>
                    {/* Bu sayfa (Standlarım detayı) bilerek salt-okunur bir genel bakış
                        (bkz. dosya başı 2026-08-13 notu) — revizyona yanıt verme/değer
                        önerme FirmaStandDetailPage.jsx'te. */}
                    <Button color="info" outline size="sm" onClick={() => setActiveRevision(r)}>
                      {tx("common:goruntule")}
                    </Button>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {activeTab === "info" && (
          <>
            <RequiredFileRibbon label={tx("standGenel.standBilgileri")}>
              <div className="w-100">
                <Row className="text-center mb-4">
                  <Col md={3}>
                    <div className="text-muted small">{tx("standGenel.salonNumarasi")}</div>
                    <div className="fw-semibold">{salonNumber}</div>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted small">{tx("standGenel.standNumarasi")}</div>
                    <div className="fw-semibold">{stand.standName}</div>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted small">{tx("standGenel.firma")}</div>
                    <div className="fw-semibold">{stand.firmaName}</div>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted small">{tx("standGenel.dekorator")}</div>
                    <div className="fw-semibold">{stand.dekoratorFirmaName || "—"}</div>
                  </Col>
                </Row>
                <Row className="text-center">
                  {[
                    { icon: Layers3, label: tx("standGenel.projeTuru", { v: standTypeName ?? "—" }) },
                    { icon: Ruler, label: tx("standGenel.stantYuksekligi", { v: stand.projectHeightNumber ? `${stand.projectHeightNumber} m` : "—" }) },
                    { icon: Maximize2, label: tx("standGenel.stantAlani", { v: stand.standArea ? `${stand.standArea} m²` : "—" }) },
                    { icon: Building2, label: tx("standGenel.katBilgisi", { v: stand.floorInfo ?? "—" }) },
                  ].map(({ icon: Icon, label }) => (
                    <Col md={3} key={label}>
                      <div
                        className="d-inline-flex align-items-center justify-content-center bg-info-subtle text-info rounded-circle mb-2"
                        style={{ width: 44, height: 44 }}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="small">{label}</div>
                    </Col>
                  ))}
                </Row>
              </div>
            </RequiredFileRibbon>

            <div className="text-muted small mt-3">{tx("standGenel.sonGuncelleme")}{" "}{fmtDate(stand.updatedAt)}</div>

            <div className="mt-3">
              <TalepOzetSection standId={standId} navigate={navigate} isLive={isLive} />
            </div>

            <SectionHeader>{tx("standGenel.denetimler")}</SectionHeader>
            <DenetimlerSection denetimler={flattenDenetimler(stand.denetimler)} />

            <SectionHeader>{tx("standGenel.fotograflar")}</SectionHeader>
            <FotograflarSection photos={stand.photos} />
          </>
        )}

        {activeTab === "konum" && (
          // WAS (until #439): <KonumTab standId={standId} stand={stand} navigate={navigate} /> — the mock Standı Gör / Alanı Gör
          <StandKonumKarti s={{ ...stand, fuarId: stand.fuarId ?? selectedFuarId }} mod="firma" />
        )}

        {activeTab === "firmalar" && (
          <div className="d-flex flex-column gap-3">
            <StantFirmalariCard firmalar={stand.firmalar} />

            {/* Yukarıdaki tablo salt-okunur (eşleme admin işi) — firma standına yeni
                bir dekoratör/taşeron eklenmesini BURADAN, serbest metinle ister;
                denetmen gerçek firmayla eşleştirir. */}
            {/* WAS (until #403): firmaId={isLive ? TEST_DECORATOR_FIRMA_ID : getCurrentFirmaId()}
                #403: live = the logged-in company (portal context). null while /portal/context loads;
                the card's own fetch effect waits for firmaId (it has it in its dependencies). */}
            <FirmaEklemeTalebiCard
              standId={stand.standId}
              fuarId={selectedFuarId}
              firmaId={isLive ? (portal?.firmaId ?? null) : getCurrentFirmaId()}
              forceLive={isLive}
            />

            <RequiredFileRibbon id="proje-anchor-commitments-section" label={tx("standGenel.insaIzniVeTaahhutnameler")}>
              <div className="w-100">
                <div className="d-flex justify-content-end mb-2">
                  <Badge
                    color={stand.hasDebt ? "danger" : "secondary"}
                    className="d-inline-flex align-items-center gap-1 fw-normal"
                    title={stand.hasDebt ? tx("standGenel.borcNedeniyleInsaatIzniVerilemez") : undefined}
                  >
                    <Coins size={11} />{" "}{tx("standGenel.borcDurumu2")}{" "}{stand.hasDebt ? tx("standGenel.var") : tx("standGenel.yok")}
                  </Badge>
                </div>
                <div className="d-flex justify-content-center mb-4">
                  <div
                    className="d-flex flex-column align-items-center justify-content-center text-white fw-bold text-center rounded-3"
                    style={{ width: 96, height: 96, background: "#178f8f", fontSize: "0.8rem", lineHeight: 1.3 }}
                  >
                    <div>{tx("standGenel.insaatIzni2")}</div>
                    <div>{izinVar ? tx("standGenel.alindi") : tx("standGenel.alinmadi")}</div>
                  </div>
                </div>
                {(stand.commitments || []).length === 0 ? (
                  <Alert color="secondary" className="mb-0">{tx("standGenel.taahhutnameBulunamadi")}</Alert>
                ) : (
                  <Row>
                    {stand.commitments.map((c) => (
                      <CommitmentTile
                        key={c.key}
                        id={`proje-anchor-commitments-${c.key}`}
                        label={c.label}
                        status={c.status}
                        level={c.level}
                        elektrikciName={c.elektrikciName}
                        file={c.fileId ? (stand.uploadedFiles || []).find((f) => f.id === c.fileId) : null}
                      />
                    ))}
                  </Row>
                )}
              </div>
            </RequiredFileRibbon>
          </div>
        )}

        {activeTab === "durumu" && (
          <StandDurumuTab
            stand={stand}
            izinVar={izinVar}
            ozet={standDurumuOzet}
            ozetLoading={ozetLoading}
            ozetError={ozetError}
            standId={standId}
            navigate={navigate}
            setActiveTab={setActiveTab}
            firmaProfile={firmaProfile}
            firmaBelgeleri={firmaBelgeleri}
          />
        )}

        {/* ─── MODAL: Revizyon Konuşması (salt-okunur) ─── */}
        <Modal isOpen={!!activeRevision} toggle={() => setActiveRevision(null)} size="lg">
          <ModalHeader toggle={() => setActiveRevision(null)}>
            {tx("standGenel.revizyon")}{" "}{activeRevision?.standFileId != null ? revisionFileLabel(activeRevision.standFileId) : tx("standGenel.projeninGeneli")}
          </ModalHeader>
          <ModalBody>
            <RevisionThread revision={activeRevision} viewerRole="company" />
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
}
